<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Card from 'primevue/card';
import Dropdown from 'primevue/dropdown';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Dialog from 'primevue/dialog';

defineOptions({
    layout: AdminMainLayout,
});

const props = defineProps({
    posts: Object,
    filters: Object,
    statusOptions: Array,
    audienceOptions: Array,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

// Reactive data
const loading = ref(false);
const search = ref(props.filters?.search || '');
const statusFilter = ref(props.filters?.status || '');
const audienceFilter = ref(props.filters?.audience || '');
const deleteDialog = ref(false);
const newsToDelete = ref({});

// Status options
const statusOptions = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'draft' },
    { label: 'Published', value: 'published' },
];

// Audience options
const audienceOptions = [
    { label: 'All', value: '' },
    { label: 'Both', value: 'both' },
    { label: 'Admin', value: 'admin' },
    { label: 'Client', value: 'client' },
];

// Format date
function formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

// Get status severity for tag
function getStatusSeverity(status) {
    return status === 'published' ? 'success' : 'warning';
}

// Get audience severity for tag
function getAudienceSeverity(audience) {
    const severities = {
        both: 'info',
        admin: 'danger',
        client: 'success',
    };
    return severities[audience] || 'secondary';
}

// Toggle published status
function toggleStatus(newsPost) {
    loading.value = true;
    router.post(route('content.news.toggle', newsPost.id), {}, {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'News status updated',
                life: 3000,
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update status',
                life: 3000,
            });
        },
        onFinish: () => {
            loading.value = false;
        },
    });
}

// Toggle pinned status
function togglePinned(newsPost) {
    loading.value = true;
    router.post(route('content.news.toggle-pin', newsPost.id), {}, {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Pin status updated',
                life: 3000,
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update pin status',
                life: 3000,
            });
        },
        onFinish: () => {
            loading.value = false;
        },
    });
}

// Show delete confirmation
function confirmDeleteNews(newsPost) {
    newsToDelete.value = newsPost;
    deleteDialog.value = true;
}

// Delete news
function deleteNews() {
    loading.value = true;
    router.delete(route('content.news.destroy', newsToDelete.value.id), {
        onSuccess: () => {
            deleteDialog.value = false;
            newsToDelete.value = {};
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'News deleted successfully',
                life: 3000,
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete news',
                life: 3000,
            });
        },
        onFinish: () => {
            loading.value = false;
        },
    });
}

// Filter data
function filterData() {
    loading.value = true;
    router.get(route('content.news.index'), {
        search: search.value || undefined,
        status: statusFilter.value || undefined,
        audience: audienceFilter.value || undefined,
    }, {
        preserveState: true,
        preserveScroll: true,
        onFinish: () => {
            loading.value = false;
        },
    });
}

// Reset filters
function resetFilters() {
    search.value = '';
    statusFilter.value = '';
    audienceFilter.value = '';
    filterData();
}
</script>

<template>
    <div class="p-4">
        <Head :title="$t('News Management')" />

        <div class="surface-card p-4 shadow-2 border-round">
            <div class="flex flex-grow text-900 font-medium text-3xl mb-4 flex align-items-center justify-content-between">
                <span>{{ $t('News Management') }}</span>
                <Link :href="route('content.news.create')" class="ml-auto no-underline">
                    <Button :label="$t('Create News')" icon="pi pi-plus" severity="primary" />
                </Link>
            </div>

            <!-- Data Table -->
            <DataTable
                :value="props.posts.data"
                :loading="loading"
                responsiveLayout="scroll"
                showGridlines
                stripedRows
                paginator
                :rows="10"
                :rowsPerPageOptions="[10, 20, 50]"
                class="p-datatable-sm"
            >
                <Column field="title" :header="$t('Title')" :sortable="true">
                    <template #body="slotProps">
                        <div class="flex flex-column">
                            <span class="font-semibold">{{ slotProps.data.title }}</span>
<!--                            <small class="text-color-secondary" v-if="slotProps.data.excerpt">-->
<!--                                {{ slotProps.data.excerpt.substring(0, 100) }}...-->
<!--                            </small>-->
                        </div>
                    </template>
                </Column>

                <Column field="status" :header="$t('Status')" :sortable="true">
                    <template #body="slotProps">
                        <Tag
                            :value="slotProps.data.status"
                            :severity="getStatusSeverity(slotProps.data.status)"
                        />
                    </template>
                </Column>

                <Column field="pinned" :header="$t('Pinned')" :sortable="true">
                    <template #body="slotProps">
                        <Button
                            :icon="slotProps.data.pinned ? 'pi pi-bookmark-fill' : 'pi pi-bookmark'"
                            :severity="slotProps.data.pinned ? 'warning' : 'secondary'"
                            text
                            rounded
                            @click="togglePinned(slotProps.data)"
                            v-tooltip="'Toggle pin'"
                        />
                    </template>
                </Column>

                <Column field="audience" :header="$t('Audience')" :sortable="true">
                    <template #body="slotProps">
                        <Tag
                            :value="slotProps.data.audience"
                            :severity="getAudienceSeverity(slotProps.data.audience)"
                        />
                    </template>
                </Column>

                <Column field="author" :header="$t('Author')">
                    <template #body="slotProps">
                        {{ slotProps.data.author?.name || 'System' }}
                    </template>
                </Column>

                <Column field="publish_at" :header="$t('Publish Date')" :sortable="true">
                    <template #body="slotProps">
                        {{ formatDate(slotProps.data.publish_at) }}
                    </template>
                </Column>

<!--                <Column field="stats_count" header="Views" :sortable="true">-->
<!--                    <template #body="slotProps">-->
<!--                        <div class="flex align-items-center gap-2">-->
<!--                            <i class="pi pi-eye text-color-secondary"></i>-->
<!--                            {{ slotProps.data.stats_count || 0 }}-->
<!--                        </div>-->
<!--                    </template>-->
<!--                </Column>-->

                <Column :header="$t('Actions')" style="width: 150px">
                    <template #body="slotProps">
                        <div class="flex gap-2">
                            <Link :href="route('content.news.edit', slotProps.data.id)">
                                <Button
                                    icon="pi pi-pencil"
                                    severity="info"
                                    text
                                    rounded
                                    size="small"
                                    v-tooltip="$t('Edit')"
                                />
                            </Link>
                            <Button
                                :icon="slotProps.data.status === 'published' ? 'pi pi-eye-slash' : 'pi pi-eye'"
                                :severity="slotProps.data.status === 'published' ? 'warning' : 'success'"
                                text
                                rounded
                                size="small"
                                @click="toggleStatus(slotProps.data)"
                                v-tooltip="slotProps.data.status === 'published' ? $t('Unpublish') : $t('Publish')"
                            />
                            <Button
                                icon="pi pi-trash"
                                severity="danger"
                                text
                                rounded
                                size="small"
                                @click="confirmDeleteNews(slotProps.data)"
                                v-tooltip="$t('Delete')"
                            />
                        </div>
                    </template>
                </Column>
            </DataTable>

            <!-- Pagination Info -->
            <div class="mt-3 text-color-secondary text-sm">
                {{ $t('Showing') }} {{ props.posts.from || 0 }} {{ $t('to') }} {{ props.posts.to || 0 }} {{ $t('of') }} {{ props.posts.total || 0 }} {{ $t('entries') }}
            </div>
        </div>
        <!-- Delete Confirmation Dialog -->
        <Dialog
            v-model:visible="deleteDialog"
            :style="{ width: '450px' }"
            :header="$t('Delete News')"
            :modal="true"
        >
            <div class="flex items-center gap-4">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500" />
                <span v-if="newsToDelete">
                    {{ $t('Are you sure you want to delete this news post') }} <b>{{ newsToDelete.title }}</b>?
                </span>
            </div>
            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    text
                    @click="deleteDialog = false"
                />
                <Button
                    :label="$t('Delete')"
                    icon="pi pi-trash"
                    severity="danger"
                    @click="deleteNews"
                />
            </template>
        </Dialog>
    </div>
</template>
